import { type Database } from "@fitness-app/supabase";

// TRE-1377 — Habit (separate entity). Typed wrappers over the generated rows, modelling the jsonb columns.

export type HabitTemplateEntity = Database["public"]["Tables"]["habit_template"]["Row"];
export type ClientHabitEntity = Database["public"]["Tables"]["client_habit"]["Row"];
export type HabitCompletionEntity = Database["public"]["Tables"]["habit_completion"]["Row"];

export enum HabitType {
  Single = "single",
  Group = "group",
  Task = "task",
}

export enum HabitCycleType {
  Infinite = "infinite",
  UntilDate = "untilDate",
  CycleNumber = "cycleNumber",
}

export enum HabitTimeType {
  AllDay = "allDay",
  Hour = "hour",
  Range = "range",
}

export enum HabitStatus {
  Active = "active",
  Inactive = "inactive",
  Upcoming = "upcoming",
}

export enum HabitCompletionStatus {
  Completed = "completed",
  Partial = "partial",
  Skipped = "skipped",
}

// dayjs day() numbering: 0 = Sunday … 6 = Saturday.
export type HabitWeekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export type HabitChecklistItem = {
  id: string;
  label: string;
};

export type HabitSchedule = {
  daysOfWeek: HabitWeekday[];
  /**
   * TRE-1656 — dated tasks (HabitType.Task): an explicit date window instead of weekly recurrence.
   * One-off task → startDate === dueDate; range task → [startDate, dueDate]. When both are absent
   * the legacy weekly daysOfWeek behaviour applies (also for tasks created before this change).
   */
  startDate?: string | null;
  dueDate?: string | null;
};

export type HabitCycle =
  | { type: HabitCycleType.Infinite }
  | { type: HabitCycleType.UntilDate; untilDate: string }
  | { type: HabitCycleType.CycleNumber; cycleLength: number; totalCycles: number };

export type HabitTime =
  | { type: HabitTimeType.AllDay }
  | { type: HabitTimeType.Hour; hour: string }
  | { type: HabitTimeType.Range; start: string; end: string };

export interface HabitTemplate extends Omit<HabitTemplateEntity, "type" | "checklist" | "schedule" | "cycle" | "time"> {
  type: HabitType;
  checklist: HabitChecklistItem[];
  schedule: HabitSchedule;
  cycle: HabitCycle;
  time: HabitTime;
}

export interface ClientHabit extends Omit<ClientHabitEntity, "status"> {
  status: HabitStatus;
}

export interface ClientHabitWithTemplate extends ClientHabit {
  template: HabitTemplate;
}

export interface HabitCompletion extends Omit<HabitCompletionEntity, "status" | "completedItems"> {
  status: HabitCompletionStatus;
  completedItems: string[];
}

export const defaultHabitSchedule: HabitSchedule = {
  daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
};

export const defaultHabitCycle: HabitCycle = {
  type: HabitCycleType.Infinite,
};

export const defaultHabitTime: HabitTime = {
  type: HabitTimeType.AllDay,
};

/** True when the habit is scheduled to be done on the given local day. */
export const isHabitScheduledOn = (schedule: HabitSchedule, weekday: HabitWeekday): boolean =>
  schedule.daysOfWeek.includes(weekday);

/**
 * TRE-1656 — the date window of a dated task, normalized so a single date fills both bounds.
 * Null when the task carries no dates (legacy weekly behaviour applies then).
 */
export const habitTaskWindow = (schedule: HabitSchedule): { start: string; end: string } | null => {
  const start = schedule.startDate ?? schedule.dueDate ?? null;
  const end = schedule.dueDate ?? schedule.startDate ?? null;
  return start && end ? { start, end } : null;
};

/** True when the dated task's deadline has passed on `date` (ISO yyyy-mm-dd). */
export const isTaskOverdueOn = (schedule: HabitSchedule, date: string): boolean => {
  const window = habitTaskWindow(schedule);
  return window != null && date > window.end;
};

/**
 * TRE-1656 — unified "should this habit/task appear on the given day" predicate.
 *
 * Habits (and tasks without dates) follow the weekly daysOfWeek schedule. A dated task shows
 * through its window; while unfinished it keeps showing past the deadline (as overdue) until done,
 * and once done it shows only inside the window plus on the day it was completed.
 */
export const isHabitDueOn = (
  habit: { type: HabitType | string; schedule: HabitSchedule; taskCompletedDate?: string | null },
  date: string,
  weekday: HabitWeekday,
): boolean => {
  if (habit.type === HabitType.Task) {
    const window = habitTaskWindow(habit.schedule);
    if (window) {
      if (habit.taskCompletedDate) {
        return (date >= window.start && date <= window.end) || date === habit.taskCompletedDate;
      }
      return date >= window.start;
    }
  }
  return isHabitScheduledOn(habit.schedule, weekday);
};
